import TencentCloudChat from "@tencentcloud/lite-chat";

// 存储 TIM 实例
let tim = null;
// 存储当前用户信息
const currentUser = {
  userID: "",
  userSig: "",
};
// 新增：本地缓存已订阅的用户ID，用于销毁时统一取消订阅
let subscribedUserIDList = [];

// 事件总线
const eventBus = {
  listeners: {},
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  },
  off(event, callback) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter(
      (cb) => cb !== callback
    );
  },
  emit(event, data) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach((cb) => {
      try {
        cb(data);
      } catch (err) {
        console.error("Event listener error:", err);
      }
    });
  },
};

// 初始化 TIM 实例
function initTIM(SDKAppID) {
  if (tim) return tim;
  tim = TencentCloudChat.create({
    SDKAppID,
  });
  // 设置日志等级 0关闭，1普通，2调试
  tim.setLogLevel(1);
  // 监听IM原生事件
  tim.on(TencentCloudChat.EVENT.SDK_READY, onReadyStateUpdate);
  tim.on(TencentCloudChat.EVENT.SDK_NOT_READY, onNotReadyStateUpdate);
  tim.on(TencentCloudChat.EVENT.KICKED_OUT, onKickedOut);
  tim.on(TencentCloudChat.EVENT.ERROR, onError);
  tim.on(TencentCloudChat.EVENT.MESSAGE_RECEIVED, onMessageReceived);
  tim.on(
    TencentCloudChat.EVENT.CONVERSATION_LIST_UPDATED,
    onConversationListUpdated
  );
  tim.on(TencentCloudChat.EVENT.USER_STATUS_UPDATED, onUserStatusUpdated);
  return tim;
}

// IM原生用户状态变更回调，转发eventBus
function onUserStatusUpdated(event) {
  console.log("【IM推送】用户在线状态变更", event.data);
  eventBus.emit("userStatusUpdated", event.data);
}

// ============ 新增：订阅用户在线状态（无轮询核心） ============
/**
 * 订阅用户状态，状态变更自动触发USER_STATUS_UPDATED推送
 * @param {string[]} userIDList 需要订阅的用户ID数组，单次最多100
 */
async function subscribeUserStatus(userIDList) {
  if (!tim) {
    return { success: false, error: "IM 实例未初始化" };
  }
  if (!Array.isArray(userIDList) || userIDList.length === 0) {
    return { success: false, error: "userIDList不能为空数组" };
  }
  try {
    const res = await tim.subscribeUserStatus({ userIDList });
    // 存入本地订阅缓存
    subscribedUserIDList = [
      ...new Set([...subscribedUserIDList, ...userIDList]),
    ];
    console.log("订阅用户状态成功", res.data);
    return { success: true, data: res.data };
  } catch (error) {
    console.error("订阅用户状态失败", error);
    return { success: false, error };
  }
}

// ============ 新增：取消订阅用户在线状态 ============
/**
 * 取消订阅用户状态；不传userIDList则清空全部订阅
 * @param {string[]} [userIDList]
 */
async function unsubscribeUserStatus(userIDList) {
  if (!tim) {
    return { success: false, error: "IM 实例未初始化" };
  }
  try {
    const options = {};
    if (userIDList && userIDList.length > 0) {
      options.userIDList = userIDList;
      subscribedUserIDList = subscribedUserIDList.filter(
        (id) => !userIDList.includes(id)
      );
    } else {
      // 清空全部订阅
      subscribedUserIDList = [];
    }
    const res = await tim.unsubscribeUserStatus(options);
    console.log("取消订阅用户状态成功", res.data);
    return { success: true, data: res.data };
  } catch (error) {
    console.error("取消订阅用户状态失败", error);
    return { success: false, error };
  }
}

// 登录
async function login(userID, userSig, SDKAppID) {
  if (!tim) {
    initTIM(SDKAppID);
  }
  const strUserID = String(userID);
  currentUser.userID = strUserID;
  currentUser.userSig = userSig;
  try {
    await tim.login({
      userID: strUserID,
      userSig,
    });
    console.log("IM 登录成功");
    return { success: true };
  } catch (error) {
    console.error("IM 登录失败:", error);
    return { success: false, error };
  }
}

// 登出
async function logout() {
  if (!tim) return;
  try {
    // 登出前清空订阅
    await unsubscribeUserStatus();
    await tim.logout();
    console.log("IM 登出成功");
    currentUser.userID = "";
    currentUser.userSig = "";
    eventBus.emit("logout");
  } catch (error) {
    console.error("IM 登出失败:", error);
  }
}

// 发送文本消息
async function sendMessage(to, text, cloudCustomData) {
  console.log(to, text);
  if (!tim) {
    return { success: false, error: "IM 实例未初始化" };
  }
  try {
    const message = tim.createTextMessage({
      to: to,
      conversationType: TencentCloudChat.TYPES.CONV_C2C,
      payload: {
        text: text,
      },
      cloudCustomData,
      offlinePushInfo: {
        pushFlag: 0,
        title: "通知",
        desc: "您有一条新的消息",
      },
    });
    const res = await tim.sendMessage(message);
    console.log("消息发送成功:", res);
    return { success: true, message: res };
  } catch (error) {
    console.error("消息发送失败:", error);
    return { success: false, error };
  }
}

// 发送图片消息
async function sendImage(to, file, cloudCustomData) {
  if (!tim) {
    return { success: false, error: "IM 实例未初始化" };
  }
  try {
    const message = tim.createImageMessage({
      to: to,
      conversationType: TencentCloudChat.TYPES.CONV_C2C,
      payload: {
        file: file,
      },
      cloudCustomData,
      onProgress: function (event) {
        console.log("file uploading:", event);
      },
      offlinePushInfo: {
        pushFlag: 0,
        title: "通知",
        desc: "您有一条新的消息",
      },
    });
    const res = await tim.sendMessage(message);
    console.log("图片消息发送成功:", res);
    return { success: true, message: res };
  } catch (error) {
    console.error("图片发送失败", error);
    return { success: false, error };
  }
}

// 撤回消息
async function revokeMessage(message) {
  if (!tim) {
    return { success: false, error: "IM 实例未初始化" };
  }
  try {
    const res = await tim.revokeMessage(message);
    return { success: true, data: res };
  } catch (error) {
    console.error("撤回消息失败", error);
    return { success: false, error };
  }
}

// 获取会话列表
async function getConversationList() {
  if (!tim) return [];
  try {
    const res = await tim.getConversationList();
    console.log(res, "sdk获取的会话列表");
    return res.data.conversationList || [];
  } catch (error) {
    console.error("获取会话列表失败:", error);
    return [];
  }
}

// 获取用户在线状态（一次性查询，仅用于打开页面初始化快照，不要循环调用！）
async function getUserStatus(userIDList) {
  if (!tim) {
    return { success: false, error: "IM 实例未初始化" };
  }
  try {
    const res = await tim.getUserStatus({ userIDList });
    return { success: true, data: res.data };
  } catch (error) {
    console.error("获取用户状态失败", error);
    return { success: false, error };
  }
}

// 获取历史消息
async function getMessageList(
  conversationID,
  nextReqMessageID = "",
  count = 15
) {
  if (!tim) return { messageList: [], isCompleted: true };
  try {
    const options = {
      conversationID: conversationID,
      count: count,
    };
    if (nextReqMessageID) {
      options.nextReqMessageID = nextReqMessageID;
    }
    const res = await tim.getMessageList(options);
    console.log(res, "获取消息列表成功");
    return res;
  } catch (error) {
    console.error("获取消息列表失败:", error);
    return {
      messageList: [],
      isCompleted: true,
    };
  }
}

// 删除会话
async function deleteConversation(conversationID) {
  if (!conversationID) {
    console.warn("deleteConversation: conversationID 不能为空");
    return {
      success: false,
      error: "conversationID 为空",
    };
  }
  if (!tim) return { success: false, error: "IM未初始化" };
  try {
    const options = {
      conversationIDList: [conversationID],
    };
    const res = await tim.deleteConversation(options);
    console.log("删除会话成功", res.data.conversation);
    return {
      success: true,
      data: res.data.conversation,
    };
  } catch (error) {
    console.error("删除会话失败:", error);
    return {
      success: false,
      error,
    };
  }
}

// 会话消息已读上报
async function setMessageRead(conversationID) {
  if (!conversationID) {
    console.warn("setMessageRead: conversationID 不能为空");
    return {
      success: false,
      error: "conversationID 为空",
    };
  }
  if (!tim) return { success: false, error: "IM未初始化" };
  try {
    const options = {
      conversationID: conversationID,
    };
    const res = await tim.setMessageRead(options);
    return {
      success: true,
      data: res.data.conversation,
    };
  } catch (error) {
    console.error("已读上报失败:", error);
    return {
      success: false,
      error,
    };
  }
}

// 销毁IM实例，释放监听
function destroyTIM() {
  if (!tim) return;
  // 销毁前取消全部订阅
  unsubscribeUserStatus();

  tim.off(TencentCloudChat.EVENT.SDK_READY, onReadyStateUpdate);
  tim.off(TencentCloudChat.EVENT.SDK_NOT_READY, onNotReadyStateUpdate);
  tim.off(TencentCloudChat.EVENT.KICKED_OUT, onKickedOut);
  tim.off(TencentCloudChat.EVENT.ERROR, onError);
  tim.off(TencentCloudChat.EVENT.MESSAGE_RECEIVED, onMessageReceived);
  tim.off(
    TencentCloudChat.EVENT.CONVERSATION_LIST_UPDATED,
    onConversationListUpdated
  );
  tim.off(TencentCloudChat.EVENT.USER_STATUS_UPDATED, onUserStatusUpdated);
  tim = null;
  currentUser.userID = "";
  currentUser.userSig = "";
  subscribedUserIDList = [];
  eventBus.listeners = {};
}

// 判断登录状态
async function isLogin() {
  if (!tim) return false;
  try {
    const res = await tim.getMyProfile();
    return !!res.data.userProfile.userID;
  } catch (e) {
    return false;
  }
}

// IM事件回调
function onReadyStateUpdate(event) {
  console.log("IM SDK Ready");
  eventBus.emit("sdkReady", event);
}
function onNotReadyStateUpdate(event) {
  console.log("IM SDK Not Ready");
  eventBus.emit("sdkNotReady", event);
}
function onKickedOut(event) {
  console.warn("被踢下线:", event);
  eventBus.emit("kickedOut", event);
}
function onError(error) {
  console.error("IM 错误:", error);
  eventBus.emit("error", error);
}
function onMessageReceived(event) {
  const messageList = event.data;
  console.log("收到新消息:", messageList);
  eventBus.emit("messageReceived", messageList);
}
function onConversationListUpdated(event) {
  const conversationList = event.data;
  console.log("会话列表更新:", conversationList);
  eventBus.emit("conversationListUpdated", conversationList);
}
/**
 * 批量订阅会话列表里所有C2C会话的对方用户（无轮询，长连接推送）
 * 自动分片，每批最多100个userID，自动去重
 * @returns {Promise<{success:boolean, subscribedUserIDs:string[], error?:any}>}
 */
async function subscribeAllConversationUserStatus() {
  if (!tim) {
    return { success: false, subscribedUserIDs: [], error: "IM实例未初始化" };
  }
  // 获取会话列表
  const conversationList = await getConversationList();
  // 提取C2C单聊会话的对方userID，过滤掉自己，去重
  const myUserId = currentUser.userID;
  const userIDSet = new Set();
  conversationList.forEach((conv) => {
    if (conv.type === TencentCloudChat.TYPES.CONV_C2C) {
      const peerId = conv.userProfile.userID;
      if (peerId && peerId !== myUserId) {
        userIDSet.add(peerId);
      }
    }
  });
  const userIDList = Array.from(userIDSet);
  if (userIDList.length === 0) {
    return { success: true, subscribedUserIDs: [], error: null };
  }

  // 分片，每片最多100
  const chunkSize = 100;
  const chunks = [];
  for (let i = 0; i < userIDList.length; i += chunkSize) {
    chunks.push(userIDList.slice(i, i + chunkSize));
  }

  const allSubscribed = [];
  for (const chunk of chunks) {
    try {
      const res = await subscribeUserStatus(chunk);
      if (res.success) {
        allSubscribed.push(...chunk);
      }
    } catch (err) {
      console.error("分片订阅失败，当前分片：", chunk, err);
    }
  }

  return {
    success: true,
    subscribedUserIDs: allSubscribed,
  };
}

/**
 * 批量取消订阅会话列表全部C2C用户
 * @returns {Promise<{success:boolean}>}
 */
async function unsubscribeAllConversationUserStatus() {
  if (!tim) {
    return { success: false, error: "IM实例未初始化" };
  }
  const conversationList = await getConversationList();
  const myUserId = currentUser.userID;
  const userIDSet = new Set();
  conversationList.forEach((conv) => {
    if (conv.type === TencentCloudChat.TYPES.CONV_C2C) {
      const peerId = conv.userProfile.userID;
      if (peerId && peerId !== myUserId) {
        userIDSet.add(peerId);
      }
    }
  });
  const userIDList = Array.from(userIDSet);
  if (userIDList.length === 0) {
    return { success: true };
  }
  // 同样分片取消订阅
  const chunkSize = 100;
  const chunks = [];
  for (let i = 0; i < userIDList.length; i += chunkSize) {
    chunks.push(userIDList.slice(i, i + chunkSize));
  }
  for (const chunk of chunks) {
    try {
      await unsubscribeUserStatus(chunk);
    } catch (err) {
      console.error("分片取消订阅失败", chunk, err);
    }
  }
  return { success: true };
}

// 导出模块
export default {
  login,
  logout,
  destroyTIM,
  sendMessage,
  sendImage,
  getConversationList,
  getMessageList,
  deleteConversation,
  setMessageRead,
  getCurrentUser() {
    return { ...currentUser };
  },
  on: eventBus.on.bind(eventBus),
  off: eventBus.off.bind(eventBus),
  isLogin,
  getTIM() {
    return tim;
  },
  getUserStatus,
  revokeMessage,
  subscribeUserStatus,
  unsubscribeUserStatus,
  subscribeAllConversationUserStatus,
  unsubscribeAllConversationUserStatus,
};
